// src/BaseCalendar.tsx
import React, { FC, useState } from 'react';
import DatePicker from '@cpg-its/ads.form.date-picker';
import dayjs from 'dayjs';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton } from '@mui/material';
import { FilterChip } from './filter-chip';
import { EventFilters } from './event-filters';
import { BaseCalendarSettings, All } from './models';

const BaseCalendar: FC<BaseCalendarSettings> = ({
    events,
    maxItems = 5,
    filterOptions = [],
    typeOptions = [],
}) => {

    const [selectedDate, setSelectedDate] = useState<string>(
        new Date().toISOString().slice(0, 10),
    );
    const toLocalDate = (s: string) => {
        const [y, m, d] = s.split('-').map(Number);
        return new Date(y, m - 1, d);
    };
    const shiftDate = (delta: number) => {
        const d = toLocalDate(selectedDate);
        d.setDate(d.getDate() + delta);
        setSelectedDate(d.toISOString().slice(0, 10));
        setItemsToShow(maxItems);
    };

    const handleDateChange = (_name: string, raw: string) => {
        const iso = dayjs(raw, 'MM/DD/YYYY').format('YYYY-MM-DD');
        setSelectedDate(iso);
        setItemsToShow(maxItems);
    };

    const [checkedCategories, setCheckedCategories] = useState<string[]>([
        All.toLowerCase(),
    ]);
    const [checkedTypes, setCheckedTypes] = useState<string[]>([
        All.toLowerCase(),
    ]);
    const [showFilters, setShowFilters] = useState<boolean>(false);

    const [itemsToShow, setItemsToShow] = useState<number>(maxItems);

    const pickStart = toLocalDate(selectedDate).getTime();
    const filtered = events.filter(ev => {
        const startMs = toLocalDate(ev.startDate).getTime();
        const endMs = ev.endDate ? toLocalDate(ev.endDate).getTime() : startMs;
        if (endMs < pickStart) return false;

        const catMatch =
            checkedCategories.includes(All.toLowerCase()) ||
            checkedCategories.includes(ev.categoryName.toLowerCase());

        const typeMatch =
            checkedTypes.includes(All.toLowerCase()) ||
            checkedTypes.includes(ev.eventTypeName.toLowerCase());

        return catMatch && typeMatch;
    });

    const displayed = filtered.slice(0, itemsToShow);
    const isLastPage = itemsToShow >= filtered.length;

  const formatDate = (s: string, includeYear = true) =>
    toLocalDate(s).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      ...(includeYear && { year: 'numeric' }),
    });

  // Label for single- or multi-day
  const getDateLabel = (start: string, end?: string) => {
    if (end && end !== start) {
      return `${formatDate(start, false)} – ${formatDate(end)}`;
    }
    return formatDate(start);
  };

    // Group events by formatted date label
    const grouped: Record<string, typeof displayed> = displayed.reduce(
        (acc, ev) => {
            const label = getDateLabel(ev.startDate, ev.endDate);
            if (!acc[label]) acc[label] = [];
            acc[label].push(ev);
            return acc;
        },
        {} as Record<string, typeof displayed>,
    );
    return (
        <div className="base-calendar">
            <div className="base-calendar-divider" />

            {/* Controls row */}
            <div className="base-calendar-controls">
                <IconButton
                    onClick={() => shiftDate(-1)}
                    size="large"
                    className="base-calendar-nav-btn"
                >
                    <ChevronLeftIcon fontSize="inherit" />
                </IconButton>

                <DatePicker
                    field={{ name: 'dateField', value: selectedDate }}
                    form={{ setFieldValue: handleDateChange }}
                    noSubText
                />

                <IconButton
                    onClick={() => shiftDate(1)}
                    size="large"
                    className="base-calendar-nav-btn"
                >
                    <ChevronRightIcon fontSize="inherit" />
                </IconButton>

                {/* Active filter chips */}
                <div className="base-calendar-active-filters">
                    {checkedCategories
                        .filter(v => v !== All.toLowerCase())
                        .map(v => {
                            const display =
                                filterOptions.find(opt => opt.toLowerCase() === v) || v;
                            return (
                                <FilterChip
                                    key={`cat-${v}`}
                                    label={display}
                                    onRemove={() => {
                                        const next = checkedCategories.filter(x => x !== v);
                                        setCheckedCategories(
                                            next.length ? next : [All.toLowerCase()],
                                        );
                                        setItemsToShow(maxItems);
                                    }}
                                />
                            );
                        })}
                    {checkedTypes
                        .filter(v => v !== All.toLowerCase())
                        .map(v => {
                            const display =
                                typeOptions.find(opt => opt.toLowerCase() === v) || v;
                            return (
                                <FilterChip
                                    key={`type-${v}`}
                                    label={display}
                                    onRemove={() => {
                                        const next = checkedTypes.filter(x => x !== v);
                                        setCheckedTypes(next.length ? next : [All.toLowerCase()]);
                                        setItemsToShow(maxItems);
                                    }}
                                />
                            );
                        })}
                </div>

                {/* Toggle filters button */}
                <button className={`base-calendar-filter-button ${showFilters ? 'active' : ''}`} type='button' onClick={() => setShowFilters(s => !s)}>
                    {showFilters ? (
                        <>
                            Close
                            <svg className='svg-icon' aria-hidden='true' focusable='false'>
                                <use xlinkHref='#close-icon' />
                            </svg>
                        </>
                    ) : (
                        <>
                            Filter By
                            <svg className='svg-icon' aria-hidden='true' focusable='false'>
                                <use xlinkHref='#filter-icon' />
                            </svg>
                        </>
                    )}
                </button>

                {/* Pop-over with filters */}
                {showFilters && (
                    <div className="base-calendar-filter-popover">
                        <EventFilters
                            name="type"
                            options={typeOptions.map(o => ({
                                filterKey: 'type',
                                filterValue: o.toLowerCase(),
                                filterDisplayValue: o,
                            }))}
                            checkedValues={checkedTypes}
                            onCheckedChange={vals => { setCheckedTypes(vals); setItemsToShow(maxItems); }}
                            label="Type of Event"
                            clearText="All"
                            allValue={All.toLowerCase()}
                        />
                        <hr />
                        <EventFilters
                            name="category"
                            options={filterOptions.map(opt => ({
                                filterKey: 'category',
                                filterValue: opt.toLowerCase(),
                                filterDisplayValue: opt,
                            }))}
                            checkedValues={checkedCategories}
                            onCheckedChange={vals => { setCheckedCategories(vals); setItemsToShow(maxItems); }}
                            label="Topic"
                            clearText="All"
                            allValue={All.toLowerCase()}
                        />
                    </div>
                )}
            </div>

            <div className="base-calendar-divider" />

            {/* Grouped events list */}

            <ul className="base-calendar-list">
                {Object.entries(grouped).map(([dateLabel, evs]) =>
                    evs.map((ev, i) => {
                        const showDate = i === 0;
                        return (
                            <li key={ev.id} className="base-calendar-item">
                                {/* 1) date  */}
                                <h2 className="base-calendar-item-date">
                                    {showDate ? (
                                        <time dateTime={ev.startDate}>{dateLabel}</time>
                                    ) : (
                                        <span aria-hidden="true">&nbsp;</span>
                                    )}
                                </h2>

                                {/* 2) details */}
                                <div className="base-calendar-item-details">
                                    <h3 className="base-calendar-item-title">
                                        <a href={ev.url}>{ev.title}</a>
                                    </h3>
                                    {ev.subtitle && (
                                        <h4 className="base-calendar-item-subtitle">
                                            {ev.subtitle}
                                        </h4>
                                    )}
                                    {!ev.hideTime && (
                                        <h5 className="base-calendar-item-time">
                                            {ev.start} – {ev.end} {ev.timezone}
                                        </h5>
                                    )}
                                    {ev.summary && (
                                        <div
                                            className="base-calendar-item-desc rich-text"
                                            dangerouslySetInnerHTML={{ __html: ev.summary }}
                                        />
                                    )}
                                </div>

                                {/* 3) thumbnail */}
                                {ev.thumbnail && (
                                    <div className="base-calendar-item-thumbnail">
                                        <a href={ev.url}>
                                            <img
                                                src={ev.thumbnail}
                                                alt={ev.title || 'Event thumbnail'}
                                            />
                                        </a>
                                    </div>
                                )}
                            </li>
                        );
                    }),
                )}

                {displayed.length === 0 ? (
                    <li className="base-calendar-empty">No events found.</li>
                ) : !isLastPage ? (
                    <li className="base-calendar-more">
                        <button
                            className="base-calendar-btn"
                            onClick={() => setItemsToShow(prev => prev + maxItems)}
                        >
                            View More Events
                        </button>
                    </li>
                ) : (
                    <li className="base-calendar-more">
                        <span className="base-calendar-empty">No more events to show</span>
                    </li>
                )}
            </ul>
        </div>
    );
};

export default BaseCalendar;
